// src/encounter/nursing-problems-view.tsx
// [PROD-3045] Frame v2 + planSave ONLY. The nurse's "Today's plan" block as STRUCTURED lines (law L10;
// R8d "Nursing care plan", Encounter Frame.dc.html 669-673): a "Nursing problems" group whose lines are
// today's own findings (./lib/nursing-problems.ts). Replaces the seven free-text boxes inside the note.
//
//   · a proposal reads "<problem> · from <block>" with two tertiary actions, "Add to plan" and "Dismiss";
//   · an accepted problem is a plain line with a tertiary "Remove";
//   · nothing qualifies → "No problems found in today's assessment". There is no text box in any state;
//   · earlier free text (an entry this view cannot read as lines) shows read-only as "Earlier note".
//
// PROD-2947 Part 2 (K9): "Interventions" — for the problems the nurse ACCEPTED, the nurse library's education
// phrases its starter map lists for them (./lib/nursing-plan-proposals.ts), by their title as written, each with
// "Add to plan" / "Dismiss"; accepted ones are plain lines with "Remove". Drawn only when there is a line to show.
// Goals are NOT drawn: the library's only goal phrase is a blank form, and a nursing goal is clinical content that
// is never invented here (content gap for the template lane). Follow-up lives in the Wrap-up block (L5).
//
// TERMINATES IN A PLAN. No order, prescription or medication control, not even a disabled one. After a
// PROVIDER sign (rule 38) the page passes `locked`: the accepted lines show as text, with no proposals
// and no actions. Free text ("Add note") is lane D's shared behaviour; `noteSlot` is its hook point.
//
// Presentational: values and the write flow in via props (the page's `useNonPrescriberPlanDraft`).
import type { ReactNode } from 'react';
import { Button, NoteLine } from '@scripsteam/scrips-signal-ds';
import { NURSING_PLAN_SECTION } from './lib/nursing-plan';
import {
  NURSING_PROBLEM_FIELD,
  acceptProblem,
  dismissProblem,
  encodeNursingProblems,
  openProposals,
  parseNursingProblems,
  removeProblem,
  type NursingProblem,
  type NursingProblemRecord,
} from './lib/nursing-problems';
import {
  NURSING_INTERVENTIONS_FIELD,
  encodeInterventions,
  libraryInterventions,
  parseInterventions,
} from './lib/nursing-plan-proposals';

export const NO_PROBLEMS_FOUND = "No problems found in today's assessment";
export const NO_PROBLEMS_ADDED = 'No nursing problems added';
export const NURSING_PROBLEMS_LABEL = 'Nursing problems';
export const EARLIER_NOTE_LABEL = 'Earlier note';
export const INTERVENTIONS_LABEL = 'Interventions';

export interface NursingProblemsProps {
  /** Today's qualifying findings (`nursingFindings`). */
  findings: readonly NursingProblem[];
  /** The plan's stored entries, keyed by the nursing section's field ids. */
  values?: Readonly<Record<string, string>>;
  /** Persist one entry. Omitted or `locked` → read-only. */
  onChange?: (fieldId: string, value: string) => void;
  locked: boolean;
  /** The stored plan is still being read: no empty-state words yet. */
  loading?: boolean;
  /** Hook point for lane D's shared "Add note" (optional free text). Absent tonight. */
  noteSlot?: ReactNode;
}

const LINE = 'm-0 text-size-sm text-text-primary';
const META = 'text-text-secondary';
/** One line: the words take the width, the tertiary actions sit at its end in one column (never wrapped
 *  under the words), so the lines read as a list. */
const ROW = 'flex items-baseline justify-between gap-3 w-full';
const ACTIONS = 'flex shrink-0 items-center gap-1';

export function NursingProblems({ findings, values, onChange, locked, loading = false, noteSlot }: NursingProblemsProps) {
  const record = parseNursingProblems(values?.[NURSING_PROBLEM_FIELD]);
  const interventions = parseInterventions(values?.[NURSING_INTERVENTIONS_FIELD]);
  const canWrite = !locked && onChange !== undefined;
  const proposals = canWrite ? openProposals(findings, record) : [];
  // K9 (Part 2): the library's interventions for the problems accepted today, not yet accepted or dismissed.
  const offeredInterventions = canWrite ? openProposals(libraryInterventions(record.accepted), interventions) : [];
  const write = (next: NursingProblemRecord) => onChange?.(NURSING_PROBLEM_FIELD, encodeNursingProblems(next));
  const writeInterventions = (next: NursingProblemRecord) => onChange?.(NURSING_INTERVENTIONS_FIELD, encodeInterventions(next));
  // Earlier free text in the plan's OTHER entries (the retired boxes): never lost, never editable here. The
  // interventions entry shows only its own earlier text — its lines are drawn above.
  const otherEarlier = NURSING_PLAN_SECTION.fields
    .filter((f) => f.id !== NURSING_PROBLEM_FIELD)
    .map((f) => ({ f, text: f.id === NURSING_INTERVENTIONS_FIELD ? interventions.earlier : (values?.[f.id] ?? '').trim() }))
    .filter(({ text }) => text !== '');
  const empty = record.accepted.length === 0 && proposals.length === 0;
  return (
    <div className="flex flex-col gap-3 w-full" data-slot="nursing-problems" data-testid="nursing-problems">
      <NoteLine label={NURSING_PROBLEMS_LABEL} align="start">
        {empty ? (
          loading ? null : (
            <p className={`${LINE} ${META}`} data-testid="nursing-problems-empty">
              {/* "None found" only when today's assessment has nothing; every finding dismissed is "none added"
                  (Codex review round 2). */}
              {canWrite && findings.length === 0 ? NO_PROBLEMS_FOUND : NO_PROBLEMS_ADDED}
            </p>
          )
        ) : (
          <PlanLines
            kind="problem"
            accepted={record.accepted}
            proposals={proposals}
            canWrite={canWrite}
            onAccept={(p) => write(acceptProblem(record, p))}
            onDismiss={(p) => write(dismissProblem(record, p))}
            onRemove={(p) => write(removeProblem(record, p))}
          />
        )}
      </NoteLine>
      {interventions.accepted.length > 0 || offeredInterventions.length > 0 ? (
        <NoteLine label={INTERVENTIONS_LABEL} align="start">
          <PlanLines
            kind="intervention"
            accepted={interventions.accepted}
            proposals={offeredInterventions}
            canWrite={canWrite}
            onAccept={(p) => writeInterventions(acceptProblem(interventions, p))}
            onDismiss={(p) => writeInterventions(dismissProblem(interventions, p))}
            onRemove={(p) => writeInterventions(removeProblem(interventions, p))}
          />
        </NoteLine>
      ) : null}
      {record.earlier !== '' || otherEarlier.length > 0 ? (
        <NoteLine label={EARLIER_NOTE_LABEL} align="start">
          <div className="flex flex-col gap-1 w-full" data-testid="nursing-plan-earlier">
            {record.earlier !== '' ? <p className={`${LINE} whitespace-pre-wrap`}>{record.earlier}</p> : null}
            {otherEarlier.map(({ f, text }) => (
              <p key={f.id} className={`${LINE} whitespace-pre-wrap`}>
                <span className={META}>{f.label}: </span>
                {text}
              </p>
            ))}
          </div>
        </NoteLine>
      ) : null}
      {noteSlot}
    </div>
  );
}

interface PlanLinesProps {
  kind: 'problem' | 'intervention';
  accepted: readonly NursingProblem[];
  proposals: readonly NursingProblem[];
  canWrite: boolean;
  onAccept: (p: NursingProblem) => void;
  onDismiss: (p: NursingProblem) => void;
  onRemove: (p: NursingProblem) => void;
}

/** One K9 list: accepted lines (text + Remove), then proposals (text · where from + Add to plan / Dismiss). */
function PlanLines({ kind, accepted, proposals, canWrite, onAccept, onDismiss, onRemove }: PlanLinesProps) {
  // A problem says the block it came from; an intervention says the problem it is for.
  const why = kind === 'problem' ? 'from' : 'for';
  return (
    <ul className="m-0 p-0 list-none flex flex-col gap-1 w-full">
      {accepted.map((p) => (
        <li key={`a-${p.text}`} className={ROW} data-testid={`nursing-${kind}-accepted`}>
          <span className={`${LINE} min-w-0 flex-1`}>{p.text}</span>
          {canWrite ? (
            <span className={ACTIONS}>
              <Button variant="tertiary" size="sm" aria-label={`Remove ${p.text}`} onClick={() => onRemove(p)}>
                Remove
              </Button>
            </span>
          ) : null}
        </li>
      ))}
      {proposals.map((p) => (
        <li key={`p-${p.text}`} className={ROW} data-testid={`nursing-${kind}-proposal`}>
          <span className={`${LINE} min-w-0 flex-1`}>
            {p.text}
            <span className={META}>
              {' '}
              · {why} {p.source}
            </span>
          </span>
          <span className={ACTIONS}>
            <Button variant="tertiary" size="sm" aria-label={`Add to plan: ${p.text}`} onClick={() => onAccept(p)}>
              Add to plan
            </Button>
            <Button variant="tertiary" size="sm" aria-label={`Dismiss ${p.text}`} onClick={() => onDismiss(p)}>
              Dismiss
            </Button>
          </span>
        </li>
      ))}
    </ul>
  );
}
